/* eslint-disable react/jsx-max-props-per-line */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import * as XLSX from 'xlsx';

const MAX_ROWS = 100;
const MAX_COLS = 20;

const ExcelPreviewPanel = ({ file }) => {
  const [rows, setRows] = useState([]);
  const [sheetName, setSheetName] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!file) {
      setRows([]);
      setSheetName('');
      setError(null);
      return undefined;
    }

    let cancelled = false;

    const parseFile = async () => {
      try {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        const firstSheet = workbook.SheetNames[0];
        if (!firstSheet) {
          if (!cancelled) {
            setRows([]);
            setError('Planilha vazia.');
          }
          return;
        }
        const sheet = workbook.Sheets[firstSheet];
        const data = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        if (!cancelled) {
          setSheetName(firstSheet);
          setRows(data.slice(0, MAX_ROWS));
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setRows([]);
          setError('Não foi possível ler a planilha.');
        }
      }
    };

    parseFile();

    return () => {
      cancelled = true;
    };
  }, [file]);

  if (!file) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary', py: 4, textAlign: 'center' }}>
        Nenhuma planilha selecionada.
      </Typography>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" sx={{ color: 'error.main', py: 4, textAlign: 'center' }}>
        {error}
      </Typography>
    );
  }

  if (!rows.length) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary', py: 4, textAlign: 'center' }}>
        Carregando planilha...
      </Typography>
    );
  }

  const colCount = Math.min(
    MAX_COLS,
    rows.reduce((max, row) => Math.max(max, row.length), 0),
  );

  return (
    <Box>
      {sheetName ? (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
          Aba: {sheetName}
          {rows.length >= MAX_ROWS ? ` (exibindo ${MAX_ROWS} primeiras linhas)` : ''}
        </Typography>
      ) : null}
      <TableContainer sx={{ maxHeight: '60vh', border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {Array.from({ length: colCount }).map((_, colIndex) => (
                <TableCell key={colIndex} sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>
                  {colIndex + 1}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {Array.from({ length: colCount }).map((_, colIndex) => (
                  <TableCell key={colIndex}>
                    {row[colIndex] != null ? String(row[colIndex]) : ''}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

ExcelPreviewPanel.propTypes = {
  file: PropTypes.any,
};

ExcelPreviewPanel.defaultProps = {
  file: null,
};

export default ExcelPreviewPanel;
